import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { PageLayout } from "../components/PageShell";
import { Placeholder } from "../components/Placeholder";
import { Figure } from "../components/Figure";
import { ContextBar } from "../components/ContextBar";
import { DeptPlacementChart } from "../components/DeptPlacementChart";
import { AlumnusCard } from "../components/AlumnusCard";
import { DEPT_ALUMNI_SHOWN, alumniFilterHas, alumniForDepartment } from "../lib/alumni";
import { ROUTES } from "../lib/routes";
import { deptPlacementSeries } from "../lib/placements";
import { DEPARTMENTS, resolveDepartment } from "../lib/departments";
import type { Department as Dept } from "../lib/departments";
import { boardName, deptBoards, type BoardMember } from "../lib/deptBoards";
import { initials } from "../lib/staff";
import { useScrollPastHero } from "../lib/useScrollPastHero";
import { useSectionTabs } from "../lib/useSectionTabs";
import { useMergedFaculty } from "../lib/mergedFaculty";
import { usePublishedDepartmentExtras } from "../lib/publishedDepartmentExtras";
import { usePublishedProgram } from "../lib/publishedProgram";
import { useDepartmentAlumni } from "../lib/publishedAlumni";
import { useDepartmentEvents } from "../lib/publishedEvents";
import { useDepartmentNews } from "../lib/publishedNews";

/** Events and news a department page lists before deferring to the full pages. */
const DEPT_EVENTS_SHOWN = 4;
const DEPT_NEWS_SHOWN = 3;
/** A head of department's message shorter than this has nothing to fold on a phone. */
const HOD_BIO_FOLD = 320;
/** Members of a board shown on a phone before "Show all". */
const BOARD_FOLD = 5;

/**
 * One numbered list in the charter block. Renders nothing when unpublished.
 *
 * `abbr` appends the short form to the heading — "… OBJECTIVES (PEO)" — so the
 * abbreviation used everywhere else on the page is stated once alongside the
 * full name.
 */
function Pillar({ title, items, abbr }: { title: string; items: string[]; abbr?: string }) {
  if (items.length === 0) return null;
  return (
    <div className="charter-group">
      <h3 className={"charter-head" + (abbr ? " has-abbr" : "")}>
        {title}
        {abbr && <span className="charter-abbr">({abbr})</span>}
      </h3>
      <ol className="charter-list">
        {items.map((t, i) => (
          <li key={i}>
            <span className="n">{String(i + 1).padStart(2, "0")}</span>
            <span className="t">{t}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}

/** Degree line for the hero and the sister cards. */
function degreeOf(d: Dept): string {
  if (d.level === "PG") return d.slug === "mca" ? "MCA" : "M.Tech";
  return d.kind === "science" ? "Applied Sciences" : `B.E. · ${d.code}`;
}


export default function Department() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  // Path param is canonical; ?dept= is kept so pre-migration links still land.
  const d = resolveDepartment(slug ?? params.get("dept") ?? "cs");
  const [heroRef, scrolledPast] = useScrollPastHero<HTMLElement>();

  const currentBioLength = d?.head?.bio.join(" ").length ?? 0;
  const publishedExtras = usePublishedDepartmentExtras(d?.code, currentBioLength);
  const program = usePublishedProgram(d?.code);
  const headBio = publishedExtras.hodMessage ? [publishedExtras.hodMessage] : d?.head?.bio ?? [];
  const headPhoto = publishedExtras.hodPhoto ?? d?.head?.photo;
  // Published cover wins over our own heroPhoto/labPhotos[0] fallback when
  // present — it's the same "official building photo" role, just a fresher
  // copy webadmin has published since.
  const heroMedia = publishedExtras.coverPhoto ?? d?.heroPhoto ?? d?.labPhotos[0];
  // The tagline and about text fall back to departments.ts where a department
  // isn't published yet. Vision, mission, PEOs and PSOs are published-only: a
  // department that publishes none of them shows no such section.
  const intro = publishedExtras.tagline ?? d?.intro ?? "";
  const about = publishedExtras.about ?? d?.about ?? [];
  const vision = publishedExtras.vision ?? undefined;
  const mission = publishedExtras.mission ?? [];
  const peos = publishedExtras.peos ?? [];
  const psos = publishedExtras.psos ?? [];
  const alumni = useDepartmentAlumni(d?.code, d?.alumni ?? []);
  const events = useDepartmentEvents(d?.code).slice(0, DEPT_EVENTS_SHOWN);
  const news = useDepartmentNews(d?.slug).slice(0, DEPT_NEWS_SHOWN);

  const { list: mergedStaff } = useMergedFaculty();
  const staff = useMemo(
    () => (d?.staffCode ? mergedStaff.filter((s) => s.deptCode.toUpperCase() === d.staffCode!.toUpperCase()) : []),
    [d, mergedStaff],
  );
  // The roster is this department's people exactly as the faculty data has
  // them: published names, order and profile links wherever webadmin covers
  // them, the staff.ts record otherwise. A department's own hand-kept list in
  // departments.ts (`publishedFaculty`) is not shown — it could only be linked
  // to profiles by guessing at name matches.
  const roster = staff;

  // Only offer nav entries for sections this department actually publishes.
  const sections = useMemo(() => {
    if (!d) return [];
    const has = {
      overview: true,
      purpose: Boolean(vision || mission.length || peos.length || psos.length),
      head: Boolean(d.head),
      faculty: roster.length > 0,
      // Infrastructure is the photo cards now; the specification table is not
      // rendered, so a department with only a table has no section.
      infrastructure: d.labPhotos.length > 0,
      placement: Boolean(d.placementBatches || d.placement),
      boards: Boolean(deptBoards(d.slug)?.bos?.length) || program.advisoryCommittee.length > 0,
      events: events.length > 0,
      news: news.length > 0,
      alumni: alumni.length > 0,
    };
    return [
      { id: "overview", label: "Overview" },
      { id: "purpose", label: "Vision & mission" },
      { id: "head", label: "Head of department" },
      { id: "faculty", label: "Faculty & staff" },
      { id: "infrastructure", label: "Infrastructure" },
      { id: "placement", label: "Placement" },
      { id: "events", label: "Events" },
      { id: "news", label: "News" },
      { id: "alumni", label: "Distinguished alumni" },
      { id: "boards", label: "Advisory board" },
      { id: "other-departments", label: "Other departments" },
    ].filter((s) => s.id === "other-departments" || has[s.id as keyof typeof has]);
  }, [d, roster.length, vision, mission, peos, psos, alumni.length, events.length, news.length, program.advisoryCommittee.length]);

  const activeId = useSectionTabs(sections.map((s) => s.id), "overview");
  // Phones only (department.css): the head of department's message opens on tap.
  const [hodOpen, setHodOpen] = useState(false);
  // Phones only: which boards (by group key) show every member rather than the first few.
  const [openBoards, setOpenBoards] = useState<Record<string, boolean>>({});

  if (!d) {
    return (
      <section className="page-hero hero-navy">
        <div className="container">
          <div className="breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span>/</span>
            <span>Not found</span>
          </div>
          <h1>Department not found.</h1>
          <p className="lead">
            No such department. <Link to={ROUTES.academics}>Browse all departments</Link> from the
            Academics page.
          </p>
        </div>
      </section>
    );
  }

  const sisters = DEPARTMENTS.filter((s) => s.kind === d.kind && s.slug !== d.slug).slice(0, 4);

  return (
    <>
      <section className="page-hero hero-navy" ref={heroRef}>
        <div className="container">
          <div className="breadcrumb">
            <Link to={ROUTES.home}>Home</Link>
            <span>/</span>
            <Link to={ROUTES.academics}>Academics</Link>
            <span>/</span>
            <span>Departments</span>
            <span>/</span>
            <span>{d.code}</span>
          </div>
          <div className="page-hero-grid">
            <div>
              <div className="dept-hero-mark">
                <span className="code">{degreeOf(d)}</span>
                {d.head && <span className="estd">{d.head.name}</span>}
              </div>
              <h1>{d.name}.</h1>
              <p className="lead">{intro}</p>
            </div>
            <div>
              {heroMedia ? (
                <Figure
                  media={heroMedia}
                  aspect="4/3"
                  fallbackLabel={`${d.code} · DEPARTMENT`}
                  sizes="(max-width: 900px) 92vw, 460px"
                />
              ) : (
                <Placeholder label={`${d.code} · DEPARTMENT`} kind="dark" aspect="4/3" />
              )}
            </div>
          </div>
        </div>
      </section>

      <ContextBar
        visible={scrolledPast}
        tag={degreeOf(d)}
        title={d.name}
        back={{ label: "Academics", to: ROUTES.academics }}
      />

      <div className="dept-detail page-tabs">
        <PageLayout
          side={[
            {
              title: "On this page",
              items: sections.map((s) => ({
                label: s.label,
                to: `#${s.id}`,
                active: activeId === s.id,
              })),
            },
          ]}
        >
          {/* ─── Overview ─────────────────────────────── */}
          <h2 id="overview">About the department</h2>
          {about.length > 0 ? (
            about.map((p, i) => <p key={i}>{p}</p>)
          ) : (
            <p className="dept-thin-note">
              The institute does not publish a description for this department.
            </p>
          )}

          <div className="dept-quickfacts">
            {/* Single code tile: webadmin's published code wins (e.g. "CSE"),
                falling back to departments.ts's own d.code where nothing's
                published yet. The department record and programme record can
                each publish their own code, but showing three tiles for one
                fact was noise — one wins. */}
            <div>
              <div className="num">{publishedExtras.publishedCode ?? d.code}</div>
              <div className="lbl">Department code</div>
            </div>
            {/* The local UG/PG tile, department-level "Faculty and staff
                strength", and department-level "Sanctioned intake" were
                removed by request now that the Programme-sourced versions
                below cover the same facts. */}
            {program.level && (
              <div>
                <div className="num">{program.level}</div>
                <div className="lbl">Programme level</div>
              </div>
            )}
            {program.facultyCount != null && (
              <div>
                <div className="num">{program.facultyCount}</div>
                <div className="lbl">Programme faculty count</div>
              </div>
            )}
            {publishedExtras.stats?.laboratoryCount != null && (
              <div>
                <div className="num">{publishedExtras.stats.laboratoryCount}</div>
                <div className="lbl">Published laboratories</div>
              </div>
            )}
            {/* From the programme record specifically — kept labelled
                "Programme" even though the department-level stats tile it
                used to sit alongside was removed by request, since it can
                still differ from departments.ts's own count for the same
                department (see publishedProgram.ts). */}
            {program.sanctionedIntake != null && (
              <div>
                <div className="num">{program.sanctionedIntake}</div>
                <div className="lbl">Programme sanctioned intake{program.intakeAcademicYear ? ` · ${program.intakeAcademicYear}` : ""}</div>
              </div>
            )}
            {program.durationLabel && (
              <div>
                <div className="num">{program.durationLabel}</div>
                <div className="lbl">Programme duration</div>
              </div>
            )}
            {publishedExtras.establishedYear && (
              <div>
                <div className="num">{publishedExtras.establishedYear}</div>
                <div className="lbl">Established</div>
              </div>
            )}
            {program.affiliatingUniversity && (
              <div>
                <div className="num">{program.affiliatingUniversity}</div>
                <div className="lbl">Affiliating university</div>
              </div>
            )}
          </div>

          {program.specialization && (
            <p><strong>Specialization — </strong>{program.specialization}</p>
          )}

          {/* Same .admit-fact card the Admissions page uses for exactly this
              pairing — forced to one column since there's only one card here. */}
          {(program.eligibility || program.admissionRoutes.length > 0) && (
            <div className="admit-facts" style={{ gridTemplateColumns: "1fr" }}>
              <div className="admit-fact">
                <div className="admit-fact-label">Eligibility &amp; admission routes</div>
                {program.eligibility && <div className="admit-fact-value">{program.eligibility}</div>}
                {program.admissionRoutes.length > 0 && (
                  <div className="admit-fact-value" style={program.eligibility ? { marginTop: 10 } : undefined}>
                    <strong>Admission routes: </strong>
                    {program.admissionRoutes.join(", ")}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ─── Vision, mission, PEOs, PSOs ────── */}
          {sections.some((s) => s.id === "purpose") && (
            <>
              <h2 id="purpose">Vision &amp; mission</h2>
              {/* Same charter treatment as the Alumni Association page. */}
              <div className={"charter" + (vision ? "" : " is-flat")}>
                {vision && (
                  <figure className="charter-vision">
                    <div className="kicker">Our vision</div>
                    <blockquote>{vision}</blockquote>
                  </figure>
                )}
                <div className="charter-pillars">
                  <Pillar title="Mission" items={mission} />
                </div>
              </div>

              {(peos.length > 0 || psos.length > 0) && (
                <>
                  <h3>Objectives</h3>
                  <div className="charter-pillars is-standalone">
                    <Pillar title="Programme Educational Objectives" abbr="PEO" items={peos} />
                    <Pillar title="Programme Specific Outcomes" abbr="PSO" items={psos} />
                  </div>
                </>
              )}

            </>
          )}

          {/* ─── Head of department ───────────────────── */}
          {d.head && (
            <>
              <h2 id="head">Head of department</h2>
              <div className="hod-card reveal">
                <div className="hod-photo">
                  {headPhoto ? (
                    <Figure
                      media={headPhoto}
                      aspect="4/5"
                      fallbackLabel={initials(d.head.name)}
                      sizes="240px"
                    />
                  ) : (
                    <Placeholder label={d.head.name.toUpperCase()} aspect="4/5" />
                  )}
                </div>
                <div>
                  <div className="kicker">Head of department</div>
                  <div className="hod-name">{d.head.name}</div>
                  <div className="hod-desig">{d.head.designation}</div>
                  {headBio.length > 0 && (
                    <div id="hod-bio" className={"hod-bio" + (hodOpen ? "" : " is-collapsed")}>
                      {headBio.map((p, i) => <p key={i}>{p}</p>)}
                    </div>
                  )}
                  {/* Hidden above phone width, where the message is always in full. */}
                  {headBio.join("").length > HOD_BIO_FOLD && (
                    <button
                      type="button"
                      className="hod-more"
                      aria-expanded={hodOpen}
                      aria-controls="hod-bio"
                      onClick={() => setHodOpen((o) => !o)}
                    >
                      {hodOpen ? "Show less" : "Read the message"} <span className="arr" aria-hidden="true">{hodOpen ? "↑" : "↓"}</span>
                    </button>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ─── Faculty ──────────────────────────────── */}
          {roster.length > 0 && (
            <>
              <h2 id="faculty">Faculty &amp; staff</h2>
              <div className="dept-section-head">
                <span className="kicker">{roster.length} people</span>
                {d.staffCode && (
                  <Link to={`${ROUTES.faculty}?dept=${d.staffCode}`} className="btn-link">
                    Open in the staff directory <span className="arr">→</span>
                  </Link>
                )}
              </div>
              <div className="dept-roster">
                {roster.map((p) => (
                  <Link key={p.slug} to={`${ROUTES.faculty}/${p.slug}`} className="dept-person">
                    <div className="dept-person-photo">
                      {p.photo ? (
                        <Figure
                          media={p.photo}
                          aspect="1/1"
                          fallbackLabel={initials(p.name)}
                          sizes="64px"
                        />
                      ) : (
                        <span className="dept-person-initials">{initials(p.name)}</span>
                      )}
                    </div>
                    <div className="dept-person-info">
                      <span className="dept-person-name">{p.name}</span>
                      <span className="dept-person-role">{p.designation}</span>
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* ─── Infrastructure ───────────────────────── */}
          {/* The specification table (S.No. / Name of Lab / Qty / Configuration
              / Software) is not rendered: it is a procurement inventory, and
              the source never ties any of its rows to a photograph. What the
              institute does label per picture is the room, so that is what the
              cards carry. */}
          {d.labPhotos.length > 0 && (
            <>
              <h2 id="infrastructure">Infrastructure</h2>
              <div className="labs-picture-grid">
                {d.labPhotos.map((m, i) => (
                  <figure key={i} className="lab-card is-static">
                    <Figure
                      media={m}
                      aspect="4/3"
                      fallbackLabel={`${d.code} · ${m.space.toUpperCase()}`}
                      sizes="(max-width: 640px) 46vw, (max-width: 900px) 30vw, 220px"
                      className="lab-thumb"
                    />
                    <figcaption className="lab-name">
                      {m.name ?? m.space} <span className="lab-room">{m.label}</span>
                    </figcaption>
                  </figure>
                ))}
              </div>
            </>
          )}

          {/* ─── Placement ────────────────────────────── */}
          {(() => {
            const series = deptPlacementSeries(d);
            if (!series.length) return null;
            return (
              <>
                <h2 id="placement">Placement</h2>
                <DeptPlacementChart rows={series} deptName={d.name} />
                <div className="dept-table-wrap">
                  <table className="programs-table dept-placement-table">
                    <caption className="sr-only">{d.name} placement record by year</caption>
                    <thead>
                      <tr>
                        <th>Year</th>
                        <th>Companies</th>
                        <th>Placed · In-campus</th>
                        <th>Placed · Off-campus</th>
                        <th>Total</th>
                        <th>Highest package</th>
                      </tr>
                    </thead>
                    <tbody>
                      {series.map((r) => (
                        <tr key={r.year}>
                          <td data-label="Year">{r.year}</td>
                          <td data-label="Companies">{r.companies ?? "—"}</td>
                          <td data-label="Placed · In-campus">{r.inCampus ?? "—"}</td>
                          <td data-label="Placed · Off-campus">{r.offCampus ?? "—"}</td>
                          <td data-label="Total">{r.total ?? "—"}</td>
                          <td data-label="Highest package">
                            {r.highestLpa === null ? "—" : `${r.highestLpa} LPA`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            );
          })()}

          {/* ─── Events & news ────────────────────────── */}
          {/* The department's own published events and news (matched on the
              published department), on the home page's cards: .events-grid
              and .event-card, which side-scroll on phones (mobile.css). A
              department with none of a kind has no section for it. */}
          {events.length > 0 && (
            <>
              <h2 id="events">Events</h2>
              <div className="events-grid dept-events">
                {events.map((e) => (
                  <Link key={e.slug} to={`${ROUTES.events}/${e.slug}`} className="event-card reveal">
                    <Figure media={e.media} fallbackLabel={e.hero} aspect="16/10" sizes="(max-width: 900px) 100vw, (max-width: 1100px) 48vw, 23vw" />
                    <div className="event-body">
                      <div className="event-meta">
                        <span className="event-tag">{e.tag}</span>
                        <span className="event-date">{e.dateLine}</span>
                      </div>
                      <h4>{e.title}</h4>
                      <div className="event-venue">
                        {e.venue}
                        {e.time ? ` · ${e.time}` : ""}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <Link className="btn-link" to={ROUTES.events}>
                All events <span className="arr">→</span>
              </Link>
            </>
          )}

          {news.length > 0 && (
            <>
              <h2 id="news">News</h2>
              <div className="events-grid dept-news">
                {news.map((n) => (
                  <Link key={n.slug} to={`${ROUTES.news}/${n.slug}`} className="event-card reveal">
                    {n.cover && (
                      <Figure media={n.cover} fallbackLabel={n.title} aspect="16/10" sizes="(max-width: 900px) 100vw, (max-width: 1100px) 48vw, 23vw" />
                    )}
                    <div className="event-body">
                      <div className="event-meta">
                        {n.category && <span className="event-tag">{n.category}</span>}
                        <span className="event-date">
                          {n.date.day} {n.date.month} {n.date.year}
                        </span>
                      </div>
                      <h4>{n.title}</h4>
                      <div className="news-card-teaser">{n.summary || n.content}</div>
                    </div>
                  </Link>
                ))}
              </div>
              <Link className="btn-link" to={ROUTES.news}>
                All news <span className="arr">→</span>
              </Link>
            </>
          )}

          {/* ─── Distinguished alumni ─────────────────── */}
          {alumni.length > 0 && (
            <>
              <h2 id="alumni">Distinguished alumni</h2>
              {/* The department's alumni exactly as published (see
                  publishedAlumni.ts), on the Alumni page's own tile, each
                  linking to its published profile on this department's pages.

                  Eight at most: two rows of four (a side-scrolling row on phones), and the link below reaches the rest.
                  Every department that has more than eight also has a tab on the
                  Alumni page, so nothing is hidden without a way to it — there
                  is a test on that. */}
              <div className="dist-grid al-dist-grid dept-dist-grid">
                {alumniForDepartment({ name: d.name, alumni }).slice(0, DEPT_ALUMNI_SHOWN).map((a, i) => (
                  <AlumnusCard
                    key={a.slug || `${a.name}-${i}`}
                    a={a}
                    showDept={false}
                    linkBase={`${ROUTES.department}/${d.slug}/alumni`}
                  />
                ))}
              </div>
              {alumniFilterHas(d.name) && (
                <Link
                  className="btn-link"
                  to={`${ROUTES.alumni}?dept=${encodeURIComponent(d.name)}`}
                >
                  All {alumni.length} {d.name} alumni <span className="arr">→</span>
                </Link>
              )}
            </>
          )}

          {/* Activities are held in departments.ts but not rendered — the
              section is hidden by request. Delete `activities` from the record
              too if it is never coming back. */}

          {/* `omitted` stays on the record as internal documentation of what
              was withheld and why — see departments.ts — but is no longer
              surfaced on the page itself, by request. */}
          {/* ─── Advisory board & assessment committee ── */}
          {(() => {
            const boards = deptBoards(d.slug);
            const groups = [
              {
                key: "bos",
                title: "Board of Studies",
                blurb:
                  "The board that reviews and updates the programme's curriculum — academics nominated by VTU and the Academic Council, industry representatives, alumni and the department's own faculty.",
                members: boards?.bos ?? [],
              },
              {
                key: "advisory-committee",
                title: "Programme Advisory Committee",
                // Published as `advisoryCommittee` on the programme record —
                // see publishedProgram.ts. This is the same Departmental
                // Advisory Board deptBoards.ts's own (still-hidden) dab[]
                // carries with fuller role/committeeRole text; wired here by
                // explicit request under its own name rather than un-hiding
                // deptBoards.ts's richer version.
                blurb:
                  "Academics, industry representatives, alumni, parents and students nominated to review the programme, as published by the department.",
                members: program.advisoryCommittee.map((m) => ({ name: m.name, role: "", committeeRole: m.designation })),
              },
            ].filter((g) => g.members.length > 0);
            if (!groups.length) return null;
            return (
              <>
                <h2 id="boards">Advisory board</h2>
                {groups.map((g) => {
                  const open = openBoards[g.key] === true;
                  return (
                    <section key={g.key} className="dept-board">
                      <h3>
                        {g.title} <span className="board-count">{g.members.length}</span>
                      </h3>
                      <p>{g.blurb}</p>
                      {/* On phones (department.css) each member is a stacked row and
                          only the first BOARD_FOLD show until "Show all". */}
                      <div className={"data-table-wrap board-table" + (open ? " is-open" : "")} id={`board-${g.key}`}>
                        <table className="data-table" role="table">
                          <thead>
                            <tr>
                              <th scope="col">Name &amp; designation</th>
                              <th scope="col">Role</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.members.map((m: BoardMember, i: number) => (
                              <tr key={`${m.name}-${i}`} role="row" className={i >= BOARD_FOLD ? "board-extra" : undefined}>
                                <th scope="row" role="rowheader">
                                  {boardName(m)}
                                  {m.role && <span className="board-desig">{m.role}</span>}
                                </th>
                                <td role="cell" className={m.committeeRole ? "board-role" : "board-role is-empty"}>{m.committeeRole ?? "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {g.members.length > BOARD_FOLD && (
                        <button
                          type="button"
                          className="board-more"
                          aria-expanded={open}
                          aria-controls={`board-${g.key}`}
                          onClick={() => setOpenBoards((o) => ({ ...o, [g.key]: !open }))}
                        >
                          {open ? "Show fewer" : `Show all ${g.members.length} members`}{" "}
                          <span className="arr" aria-hidden="true">{open ? "↑" : "↓"}</span>
                        </button>
                      )}
                    </section>
                  );
                })}
              </>
            );
          })()}


          {/* The SWOT is held in departments.ts but not rendered — the section is
              hidden by request, on every department. Delete `swot` from the
              records too if it is never coming back. */}

          {/* ─── Sister departments ───────────────────── */}
          <h2 id="other-departments">Other departments</h2>
          <div className="sister-grid">
            {sisters.map((s) => (
              <Link key={s.slug} to={`${ROUTES.department}/${s.slug}`} className="sister-card">
                <div className="sc-code">{degreeOf(s)}</div>
                <div className="sc-name">{s.name}</div>
              </Link>
            ))}
          </div>
        </PageLayout>
      </div>
    </>
  );
}
